import type { InputDefinition, InputType } from '../components/common/PromptEditor';

export type RuntimeInputSource = 'shared_input' | 'expert' | 'direct_input' | 'legacy';

export interface RuntimeInputRef {
  key: string;
  name: string;
  type: InputType;
  source: RuntimeInputSource;
  runtimeNamespace?: string;
  expertId?: number;
  expertName?: string;
  description?: string;
}

const KNOWN_INPUT_TYPES = new Set<string>([
  'expert',
  'indicator',
  'economic_calendar',
  'news_data',
  'cot_data',
  'options_data',
  'economic_indicators',
  'account_data',
  'open_positions',
  'pending_orders',
  'symbol_data',
  'trading_journal',
  'price_data',
  'previous_expert_results',
  'current_time',
  'static_text',
  'chart_image',
  'chart_image_file',
  'storage_text',
  'storage_file',
  'global_storage_text',
  'global_storage_file',
  'knowledge_graph',
  'model_lab_prediction',
  'stock_fundamentals',
  'openbb_news',
  'openbb_options',
  'openbb_forex',
  'openbb_crypto',
  'openbb_etf',
  'openbb_economic',
]);

const INPUT_TYPE_LABELS: Record<string, string> = {
  expert: 'Expert output',
  indicator: 'Indicator',
  economic_calendar: 'Economic calendar',
  news_data: 'News data',
  cot_data: 'COT data',
  options_data: 'Options data',
  economic_indicators: 'Economic indicators',
  account_data: 'Account data',
  open_positions: 'Open positions',
  pending_orders: 'Pending orders',
  symbol_data: 'Symbol data',
  trading_journal: 'Trading journal',
  price_data: 'Price data',
  previous_expert_results: 'Previous expert results',
  current_time: 'Current time',
  static_text: 'Static text',
  chart_image: 'Chart image',
  chart_image_file: 'Chart image file',
  storage_text: 'Storage text',
  storage_file: 'Storage file',
  global_storage_text: 'Global storage text',
  global_storage_file: 'Global storage file',
  knowledge_graph: 'Knowledge graph',
  model_lab_prediction: 'Model Lab prediction',
  stock_fundamentals: 'Stock fundamentals',
  openbb_news: 'OpenBB news',
  openbb_options: 'OpenBB options',
  openbb_forex: 'OpenBB forex',
  openbb_crypto: 'OpenBB crypto',
  openbb_etf: 'OpenBB ETF',
  openbb_economic: 'OpenBB economic data',
};

const SOURCE_LABELS: Record<RuntimeInputSource, string> = {
  shared_input: 'Shared',
  expert: 'Expert',
  direct_input: 'Direct',
  legacy: 'Legacy',
};

const toRecord = (value: unknown): Record<string, any> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, any>) : {};

const toInputType = (value: unknown, fallback: InputType = 'static_text'): InputType => {
  const candidate = String(value || '').trim();
  if (candidate && KNOWN_INPUT_TYPES.has(candidate)) {
    return candidate as InputType;
  }
  return fallback;
};

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

export const getRuntimeInputPacket = (inputData?: Record<string, any> | null): Record<string, any> | null => {
  const packet = toRecord(inputData).runtime_input_packet;
  return packet && typeof packet === 'object' && !Array.isArray(packet)
    ? (packet as Record<string, any>)
    : null;
};

export const hasRuntimeInputPacket = (inputData?: Record<string, any> | null): boolean =>
  Boolean(getRuntimeInputPacket(inputData));

export const getInputTypeLabel = (type?: string): string => {
  const normalized = String(type || '').trim();
  return INPUT_TYPE_LABELS[normalized] || normalized || 'Input';
};

export const getInputSourceLabel = (source?: RuntimeInputSource | string): string => {
  const normalized = String(source || '').trim() as RuntimeInputSource;
  return SOURCE_LABELS[normalized] || normalized || 'Input';
};

export const getLegacyInputDefinitions = (inputData?: Record<string, any> | null): InputDefinition[] => {
  const inputs = toRecord(inputData).inputs;
  if (!Array.isArray(inputs)) return [];

  return inputs.map((input: any) => ({
    name: input?.name || '',
    type: toInputType(input?.type),
    description: input?.description,
    expert_id: input?.expert_id,
    expert_name: input?.expert_name,
    indicator_name: input?.indicator_name,
    indicators: input?.indicators,
    use_indicator_set: input?.use_indicator_set,
    indicator_set_id: input?.indicator_set_id,
    symbol: input?.symbol,
    symbols: input?.symbols,
    timeframe: input?.timeframe,
    data_type: input?.data_type,
    source: input?.source,
    account_id: input?.account_id,
    account_ids: input?.account_ids,
    currency: input?.currency,
    count: input?.count,
    impact: input?.impact,
    impacts: input?.impacts,
    asset_type: input?.asset_type,
    entry_id: input?.entry_id,
    label: input?.label,
    text: input?.text,
    currencies: input?.currencies,
    economic_indicator_names: input?.economic_indicator_names,
    include_comparison: input?.include_comparison,
    option_type: input?.option_type,
    near_the_money: input?.near_the_money,
    max_expirations: input?.max_expirations,
    include_greeks: input?.include_greeks,
    expiration_date: input?.expiration_date,
    duration_days: input?.duration_days,
  }));
};

export const getLegacyInputRefs = (inputData?: Record<string, any> | null): RuntimeInputRef[] =>
  getLegacyInputDefinitions(inputData).map((input, index) => {
    const source: RuntimeInputSource = input.type === 'expert' ? 'expert' : 'direct_input';
    const name = input.name || input.expert_name || `${input.type}_${index + 1}`;

    return {
      key: name,
      name,
      type: input.type,
      source,
      expertId: input.expert_id,
      expertName: input.expert_name,
      description: input.description,
    };
  });

export const getRuntimeInputRefs = (inputData?: Record<string, any> | null): RuntimeInputRef[] => {
  const packet = getRuntimeInputPacket(inputData);
  if (!packet) return [];

  const preparedRefs = packet.prepared_input_refs;
  if (!Array.isArray(preparedRefs)) return [];

  return preparedRefs
    .filter((ref: any) => ref && typeof ref === 'object')
    .map((ref: any, index: number): RuntimeInputRef => {
      const source = String(ref.source || '').trim() as RuntimeInputSource;
      const normalizedSource: RuntimeInputSource =
        source === 'shared_input' || source === 'expert' || source === 'direct_input'
          ? source
          : 'legacy';
      const fallbackType: InputType = normalizedSource === 'expert' ? 'expert' : 'static_text';
      const type = toInputType(ref.input_type, fallbackType);
      const key = String(ref.key || ref.name || ref.runtime_namespace || `${type}_${index + 1}`);
      const name = String(ref.name || ref.key || ref.runtime_namespace || key);

      return {
        key,
        name,
        type,
        source: normalizedSource,
        runtimeNamespace: ref.runtime_namespace,
        expertId: toNumber(ref.expert_id),
        expertName: ref.expert_name,
        description: ref.reason,
      };
    });
};

export const getEffectiveInputRefs = (inputData?: Record<string, any> | null): RuntimeInputRef[] => {
  const refs = [...getRuntimeInputRefs(inputData), ...getLegacyInputRefs(inputData)];
  const refsByKey = new Map<string, RuntimeInputRef>();

  refs.forEach((ref) => {
    const dedupeKey = [
      ref.source,
      ref.type,
      String(ref.name || ref.key || '').toLowerCase(),
    ].join(':');

    const existing = refsByKey.get(dedupeKey);
    if (!existing) {
      refsByKey.set(dedupeKey, ref);
      return;
    }

    refsByKey.set(dedupeKey, {
      ...existing,
      expertId: existing.expertId || ref.expertId,
      expertName: existing.expertName || ref.expertName,
      runtimeNamespace: existing.runtimeNamespace || ref.runtimeNamespace,
      description: existing.description || ref.description,
    });
  });

  return Array.from(refsByKey.values());
};

export const getExpertDependencyRefs = (inputData?: Record<string, any> | null): RuntimeInputRef[] =>
  getEffectiveInputRefs(inputData).filter((ref) => ref.source === 'expert' && ref.expertId);

export const describeInputRef = (ref: RuntimeInputRef): string =>
  `${ref.name} (${getInputSourceLabel(ref.source)} ${getInputTypeLabel(ref.type)})`;

export const getExpertInputSummary = (inputData?: Record<string, any> | null) => {
  const refs = getEffectiveInputRefs(inputData);
  const sharedCount = refs.filter((ref) => ref.source === 'shared_input').length;
  const upstreamCount = refs.filter((ref) => ref.source === 'expert').length;
  const directCount = refs.filter((ref) => ref.source === 'direct_input').length;

  return {
    refs,
    total: refs.length,
    sharedCount,
    upstreamCount,
    directCount,
    hasRuntimePacket: hasRuntimeInputPacket(inputData),
    label: refs.length === 0 ? 'No inputs' : `${refs.length} input${refs.length === 1 ? '' : 's'}`,
    detail: [
      sharedCount ? `${sharedCount} shared` : '',
      upstreamCount ? `${upstreamCount} upstream` : '',
      directCount ? `${directCount} direct` : '',
    ].filter(Boolean).join(', '),
    lines: refs.map(describeInputRef),
  };
};
